import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiChevronLeft, FiChevronRight, FiMessageSquare } = FiIcons;

const testimonials = [
  { id: 1, name: 'Mohammad Ashraf', role: 'Home Owner', location: 'Baramulla', rating: 5, review: 'Tayyab Electric Studio did a complete wiring job for my new house. The work was clean, professional and finished on time. Very happy with the quality and the team was very respectful. Highly recommended!', service: 'Electrical Wiring', color: 'from-blue-500 to-cyan-500', initials: 'MA' },
  { id: 2, name: 'Riyaz Ahmed', role: 'Shop Owner', location: 'Sopore', rating: 5, review: 'Got CCTV cameras installed for my shop. The installation was done perfectly, remote access works great on my phone. Any time I had a question they responded immediately on WhatsApp. Top service!', service: 'CCTV Installation', color: 'from-purple-500 to-pink-500', initials: 'RA' },
  { id: 3, name: 'Farooq Mir', role: 'Business Owner', location: 'Fatehgarh', rating: 5, review: 'We signed up for the Premium Care Plan and it has been worth every rupee. Monthly inspections, quick response whenever something goes wrong. Our office has had zero electrical issues since.', service: 'Premium Care Plan', color: 'from-yellow-500 to-amber-500', initials: 'FM' },
  { id: 4, name: 'Shabir Lone', role: 'Home Owner', location: 'Pattan', rating: 5, review: 'Solar panel installation was done very professionally. They explained everything clearly and the system has been working perfectly. Our electricity bill dropped significantly. Very satisfied!', service: 'Solar Power System', color: 'from-green-500 to-teal-500', initials: 'SL' },
  { id: 5, name: 'Tariq Wani', role: 'Hotel Manager', location: 'Baramulla', rating: 5, review: 'Smart home automation was installed in our hotel rooms. Guests are very impressed with the lighting control and smart locks. The team worked with minimal disruption to our operations. Excellent work!', service: 'Smart Home Automation', color: 'from-indigo-500 to-purple-500', initials: 'TW' },
  { id: 6, name: 'Amir Bhat', role: 'Office Admin', location: 'Sopore', rating: 5, review: 'Structured cabling and WiFi setup for our 3-floor office building. Coverage is now perfect everywhere. The work was very neat and they cleaned up after themselves. Would definitely hire again.', service: 'Networking & Cabling', color: 'from-cyan-500 to-blue-500', initials: 'AB' },
  { id: 7, name: 'Gulzar Khan', role: 'Home Owner', location: 'Rafiabad', rating: 5, review: 'Called them for emergency repair at night — they responded within the hour. Fixed the issue quickly and charged very fairly. This kind of 24/7 service is rare. Will always call Tayyab Electric first!', service: 'Emergency Repair', color: 'from-red-500 to-orange-500', initials: 'GK' },
  { id: 8, name: 'Naseer Ahmad', role: 'Clinic Owner', location: 'Baramulla', rating: 5, review: 'Inverter and UPS installation for our medical clinic. They understood the critical nature of our work and delivered a reliable backup system. No power cuts affect our operations anymore. Thank you!', service: 'Inverter & UPS', color: 'from-teal-500 to-green-500', initials: 'NA' },
];

const StarRating = ({ rating }) => (
  <div className="flex space-x-0.5">
    {[...Array(5)].map((_, i) => (
      <SafeIcon key={i} icon={FiStar} className={`text-sm ${i < rating ? 'text-yellow-400' : 'text-gray-600'}`}
        style={i < rating ? { fill: '#facc15' } : {}} />
    ))}
  </div>
);

const TestimonialCard = ({ testimonial, isCenter }) => (
  <div
    className={`relative rounded-2xl p-6 border transition-all duration-500 flex-shrink-0 ${isCenter ? 'border-blue-500/50 shadow-2xl shadow-blue-500/10' : 'border-gray-800/60 opacity-60'}`}
    style={{
      width: 320,
      transform: isCenter ? 'scale(1.05)' : 'scale(0.95)',
      background: isCenter ? 'linear-gradient(135deg, rgba(0,102,255,0.08), rgba(0,204,255,0.04))' : 'linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))',
      backdropFilter: 'blur(10px)',
    }}
  >
    <div className="absolute top-4 right-5 text-5xl font-black text-blue-500/10 leading-none select-none">"</div>
    <div className="flex items-center space-x-3 mb-4">
      <div className={`w-11 h-11 bg-gradient-to-br ${testimonial.color} rounded-full flex items-center justify-center flex-shrink-0 shadow-lg`}>
        <span className="text-white font-bold text-sm">{testimonial.initials}</span>
      </div>
      <div>
        <p className="text-white font-semibold text-sm">{testimonial.name}</p>
        <p className="text-gray-500 text-xs">{testimonial.role} · {testimonial.location}</p>
      </div>
    </div>
    <StarRating rating={testimonial.rating} />
    <p className="text-gray-300 text-sm leading-relaxed mt-3 mb-4">"{testimonial.review}"</p>
    <div className="inline-flex items-center space-x-1.5 rounded-full px-3 py-1" style={{ background: 'rgba(0,102,255,0.1)' }}>
      <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${testimonial.color}`} />
      <span className="text-xs text-gray-400">{testimonial.service}</span>
    </div>
    {isCenter && <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-2/3 rounded-full bg-gradient-to-r ${testimonial.color}`} />}
  </div>
);

const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const intervalRef = useRef(null);

  const next = () => setCurrent(i => (i + 1) % testimonials.length);
  const prev = () => setCurrent(i => (i - 1 + testimonials.length) % testimonials.length);

  useEffect(() => {
    if (autoPlay) intervalRef.current = setInterval(next, 4000);
    else clearInterval(intervalRef.current);
    return () => clearInterval(intervalRef.current);
  }, [autoPlay, current]);

  const len = testimonials.length;
  const visible = [
    testimonials[(current - 1 + len) % len],
    testimonials[current],
    testimonials[(current + 1) % len],
  ];

  return (
    // ✅ FIXED: reduced py-24 to py-12 to remove big black gap
    <section className="relative py-12 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #000000 0%, #020b1a 50%, #000000 100%)' }}>

      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(0,102,255,0.4), transparent)' }} />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(0,102,255,0.4), transparent)' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-3xl opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #0066ff, transparent)' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }} className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300 mb-4">
            <SafeIcon icon={FiMessageSquare} className="text-lg" />
            <span>Customer Reviews</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">What Our Clients</span>{' '}
            <span className="text-white">Say About Us</span>
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto">Real feedback from real customers across Kashmir. We let our work speak for itself.</p>
          <div className="inline-flex items-center space-x-3 mt-6 bg-gray-900/60 border border-gray-700/50 rounded-full px-5 py-2">
            <div className="flex space-x-0.5">
              {[...Array(5)].map((_, i) => (
                <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-sm" style={{ fill: '#facc15' }} />
              ))}
            </div>
            <span className="text-white font-bold text-sm">5.0</span>
            <span className="text-gray-500 text-xs">· {testimonials.length} Reviews</span>
          </div>
        </motion.div>

        <div className="relative flex items-center justify-center" onMouseEnter={() => setAutoPlay(false)} onMouseLeave={() => setAutoPlay(true)}>
          <button onClick={prev} className="absolute left-0 z-10 w-10 h-10 bg-gray-800/80 hover:bg-blue-600/40 border border-gray-700/50 hover:border-blue-500/50 rounded-full items-center justify-center text-gray-300 hover:text-white transition-all duration-300 hidden md:flex">
            <SafeIcon icon={FiChevronLeft} className="text-lg" />
          </button>
          <div className="flex items-center justify-center space-x-4 w-full px-0 md:px-16 overflow-hidden">
            <AnimatePresence mode="popLayout">
              {visible.map((t, i) => (
                <motion.div key={`${t.id}-${i}`} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.4, ease: 'easeInOut' }} className={i !== 1 ? 'hidden md:block' : ''}>
                  <TestimonialCard testimonial={t} isCenter={i === 1} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          <button onClick={next} className="absolute right-0 z-10 w-10 h-10 bg-gray-800/80 hover:bg-blue-600/40 border border-gray-700/50 hover:border-blue-500/50 rounded-full items-center justify-center text-gray-300 hover:text-white transition-all duration-300 hidden md:flex">
            <SafeIcon icon={FiChevronRight} className="text-lg" />
          </button>
        </div>

        <div className="flex items-center justify-center space-x-2 mt-8">
          {testimonials.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`rounded-full transition-all duration-300 ${i === current ? 'w-6 h-2 bg-blue-400' : 'w-2 h-2 bg-gray-700 hover:bg-gray-500'}`} />
          ))}
        </div>

        <div className="max-w-xs mx-auto mt-4 h-px bg-gray-800 rounded-full overflow-hidden">
          <motion.div key={current} initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: 4, ease: 'linear' }} className="h-full bg-gradient-to-r from-blue-500 to-cyan-400" />
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} viewport={{ once: true }} className="text-center mt-8">
          <p className="text-gray-500 text-sm mb-4">Join 1000+ happy customers across Kashmir</p>
          <button onClick={() => window.dispatchEvent(new CustomEvent('openQuoteModal'))}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-full font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300">
            <span>Get Your Free Quote</span>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;